import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { API_URL } from "../constants";

const isDev = process.env.NODE_ENV === "development";
const log = (...args: unknown[]) => isDev && console.log(...args);

/**
 * Нормализует серверную ошибку: сообщение из поля `error` и HTTP-статус.
 * Вызывающий код может различать 403/409 и т.п. по `status`.
 * Без серверного сообщения возвращает исходную ошибку без изменений.
 */
export function normalizeApiError(
  error: AxiosError,
): Error & { status?: number } {
  const serverMessage = (error.response?.data as { error?: string })?.error;
  if (!serverMessage) {
    return error;
  }
  const normalizedError = new Error(serverMessage) as Error & {
    status?: number;
  };
  normalizedError.status = error.response?.status;
  return normalizedError;
}

/**
 * API клиент с автоматическим обновлением токена
 */
class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Добавляем токен к каждому запросу
    this.client.interceptors.request.use((config) => {
      const tokenFromLocal = localStorage.getItem("access_token");
      const tokenFromSession = sessionStorage.getItem("access_token");
      const token = tokenFromLocal || tokenFromSession;

      log("[API Client] Request:", {
        url: config.url,
        method: config.method,
        hasTokenInLocal: !!tokenFromLocal,
        hasTokenInSession: !!tokenFromSession,
        tokenUsed: token ? `${token.substring(0, 20)}...` : "none",
      });

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Обрабатываем ошибки и обновляем токен при необходимости
    this.client.interceptors.response.use(
      (response) => {
        log("[API Client] Response:", {
          url: response.config.url,
          status: response.status,
        });
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        log("[API Client] Error:", {
          url: originalRequest?.url,
          status: error.response?.status,
          message: error.message,
          _retry: originalRequest?._retry,
        });

        const isAuthRequest = originalRequest?.url?.startsWith("/auth/");

        // Если 401 и это не auth запрос, и мы еще не пробовали retry
        if (
          error.response?.status === 401 &&
          !isAuthRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          if (this.isRefreshing) {
            // Ждем завершения текущего refresh
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.client.request(originalRequest));
              });
            });
          }

          this.isRefreshing = true;

          const refreshTokenFromLocal = localStorage.getItem("refresh_token");
          const refreshTokenFromSession =
            sessionStorage.getItem("refresh_token");
          const refreshToken = refreshTokenFromLocal || refreshTokenFromSession;
          const storage = refreshTokenFromLocal ? localStorage : sessionStorage;

          log("[API Client] 401 Error - Attempting token refresh:", {
            hasRefreshTokenInLocal: !!refreshTokenFromLocal,
            hasRefreshTokenInSession: !!refreshTokenFromSession,
          });

          if (refreshToken) {
            try {
              log("[API Client] Refreshing token...");
              const response = await this.client.post("/auth/refresh", {
                refresh_token: refreshToken,
              });
              const { access_token, refresh_token } = response.data;
              storage.setItem("access_token", access_token);
              storage.setItem("refresh_token", refresh_token);

              log("[API Client] Token refreshed successfully");

              // Уведомляем всех подписчиков
              this.refreshSubscribers.forEach((cb) => cb(access_token));
              this.refreshSubscribers = [];

              originalRequest.headers.Authorization = `Bearer ${access_token}`;
              return this.client.request(originalRequest);
            } catch (refreshError) {
              log("[API Client] Token refresh failed:", refreshError);
              this.clearAuth();
            } finally {
              this.isRefreshing = false;
              this.refreshSubscribers = [];
            }
          } else {
            log("[API Client] No refresh token found");
            this.clearAuth();
            this.isRefreshing = false;
          }
        }
        // Извлекаем сообщение об ошибке из ответа сервера (Epic 10)
        return Promise.reject(normalizeApiError(error));
      },
    );
  }

  private clearAuth() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    document.cookie =
      "is_authenticated=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    log("[API Client] Tokens cleared, notifying layout");
    window.dispatchEvent(new Event("auth:logout"));
  }

  get instance() {
    return this.client;
  }
}

export const apiClient = new ApiClient().instance;
